import React, { useMemo } from "react";
import { MessageCircleQuestion } from "lucide-react";

const clamp = (value, min, max) => {
  if (Number.isNaN(value)) {
    return min;
  }
  return Math.min(Math.max(value, min), max);
};

const normalizeColorString = (value) => value?.trim().toLowerCase() ?? "";

const parseHexColor = (value) => {
  const hexMatch = value.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!hexMatch) {
    return null;
  }

  let hex = hexMatch[1];
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => `${char}${char}`)
      .join("");
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  if ([r, g, b].some((component) => Number.isNaN(component))) {
    return null;
  }

  return { r, g, b };
};

const parseRgbColor = (value) => {
  const rgbMatch = value.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(\d*\.?\d+))?\s*\)$/i
  );
  if (!rgbMatch) {
    return null;
  }

  const r = clamp(Number.parseInt(rgbMatch[1], 10), 0, 255);
  const g = clamp(Number.parseInt(rgbMatch[2], 10), 0, 255);
  const b = clamp(Number.parseInt(rgbMatch[3], 10), 0, 255);

  return { r, g, b };
};

const parseColor = (value) => {
  if (!value) {
    return null;
  }

  const normalized = normalizeColorString(value);
  const hexColor = parseHexColor(normalized);
  if (hexColor) {
    return hexColor;
  }

  const rgbColor = parseRgbColor(normalized);
  if (rgbColor) {
    return rgbColor;
  }

  return null;
};

const rgbToHex = ({ r, g, b }) => {
  const toHex = (component) => {
    const hex = clamp(Math.round(component), 0, 255).toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const mixColors = (color, target, amount) => {
  const ratio = clamp(amount, 0, 1);
  return {
    r: Math.round(color.r + (target.r - color.r) * ratio),
    g: Math.round(color.g + (target.g - color.g) * ratio),
    b: Math.round(color.b + (target.b - color.b) * ratio),
  };
};

const getRelativeLuminance = ({ r, g, b }) => {
  const srgb = [r, g, b].map((component) => {
    const channel = component / 255;
    if (channel <= 0.03928) {
      return channel / 12.92;
    }
    return ((channel + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
};

const toRgbaString = (color, alpha) => {
  const normalizedAlpha = clamp(alpha, 0, 1);
  return `rgba(${clamp(Math.round(color.r), 0, 255)}, ${clamp(
    Math.round(color.g),
    0,
    255
  )}, ${clamp(Math.round(color.b), 0, 255)}, ${normalizedAlpha})`;
};

const QUICK_QUESTION_DARK_TARGET = "#0f172a";
const QUICK_QUESTION_LIGHT_TARGET_DARK_MODE = "#f8fafc";
const QUICK_QUESTION_LIGHT_TARGET_LIGHT_MODE = "#f9fafb";
const QUICK_QUESTION_NEUTRAL_TARGET_DARK_MODE = "#334155";
const QUICK_QUESTION_NEUTRAL_TARGET_LIGHT_MODE = "#e2e8f0";
const QUICK_QUESTION_LUMINANCE_LIGHT_THRESHOLD = 0.6;
const QUICK_QUESTION_LUMINANCE_DARK_THRESHOLD = 0.35;

const QUICK_QUESTION_FALLBACK_LIGHT = {
  background: "#f8fafc",
  hoverBackground: "#f1f5f9",
  border: "#e2e8f0",
  hoverBorder: "#cbd5f5",
  shadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
  hoverShadow: "0 6px 20px rgba(15, 23, 42, 0.14)",
};

const QUICK_QUESTION_FALLBACK_DARK = {
  background: "#1f2937",
  hoverBackground: "#27364a",
  border: "#334155",
  hoverBorder: "#3b4c63",
  shadow: "0 4px 12px rgba(15, 23, 42, 0.45)",
  hoverShadow: "0 6px 20px rgba(15, 23, 42, 0.55)",
};

const getFallbackPalette = (isDark) =>
  isDark ? QUICK_QUESTION_FALLBACK_DARK : QUICK_QUESTION_FALLBACK_LIGHT;

const getQuickQuestionPalette = (textColor, isDark) => {
  const parsedTextColor = parseColor(textColor);
  if (!parsedTextColor) {
    return getFallbackPalette(isDark);
  }

  const luminance = getRelativeLuminance(parsedTextColor);
  const darkTarget = parseColor(QUICK_QUESTION_DARK_TARGET) ?? { r: 15, g: 23, b: 42 };
  const lightTarget =
    parseColor(isDark ? QUICK_QUESTION_LIGHT_TARGET_DARK_MODE : QUICK_QUESTION_LIGHT_TARGET_LIGHT_MODE) ?? {
      r: 249,
      g: 250,
      b: 251,
    };
  const neutralTarget =
    parseColor(isDark ? QUICK_QUESTION_NEUTRAL_TARGET_DARK_MODE : QUICK_QUESTION_NEUTRAL_TARGET_LIGHT_MODE) ?? {
      r: 226,
      g: 232,
      b: 240,
    };

  let baseColor;
  let hoverColor;

  if (luminance >= QUICK_QUESTION_LUMINANCE_LIGHT_THRESHOLD) {
    const mixAmount = isDark ? 0.5 : 0.7;
    const hoverAmount = Math.min(1, mixAmount + 0.15);
    baseColor = mixColors(parsedTextColor, darkTarget, mixAmount);
    hoverColor = mixColors(parsedTextColor, darkTarget, hoverAmount);
  } else if (luminance <= QUICK_QUESTION_LUMINANCE_DARK_THRESHOLD) {
    const mixAmount = isDark ? 0.9 : 0.8;
    const hoverAmount = Math.min(1, mixAmount + (isDark ? 0.05 : 0.12));
    baseColor = mixColors(parsedTextColor, lightTarget, mixAmount);
    hoverColor = mixColors(parsedTextColor, lightTarget, hoverAmount);
  } else {
    const mixAmount = isDark ? 0.45 : 0.6;
    const hoverAmount = Math.min(1, mixAmount + 0.1);
    baseColor = mixColors(parsedTextColor, neutralTarget, mixAmount);
    hoverColor = mixColors(parsedTextColor, neutralTarget, hoverAmount);
  }

  const borderColor = mixColors(parsedTextColor, baseColor, 0.65);
  const hoverBorderColor = mixColors(parsedTextColor, hoverColor, 0.7);

  const shadow = `0 4px 12px ${toRgbaString(parsedTextColor, isDark ? 0.28 : 0.12)}`;
  const hoverShadow = `0 6px 20px ${toRgbaString(parsedTextColor, isDark ? 0.35 : 0.18)}`;

  return {
    background: rgbToHex(baseColor),
    hoverBackground: rgbToHex(hoverColor),
    border: rgbToHex(borderColor),
    hoverBorder: rgbToHex(hoverBorderColor),
    shadow,
    hoverShadow,
  };
};

const QuickQuestions = ({
  questions = [],
  isDark = false,
  primaryColor = "#2563eb",
  onQuestionClick,
  isTyping = false,
  layout = "vertical",
  fontFamily = "Inter",
  fontSize = "14px",
  textColor,
  isRtl = false,
  questionTextColor,
  questionBoxColor,
}) => {
  if (!questions.length || isTyping) {
    return null;
  }

  const resolvedTextColor =
    questionTextColor ?? textColor ?? (isDark ? "#f3f4f6" : "#111827");
  const resolvedQuestionBoxColor =
    questionBoxColor ?? (isDark ? "#374151" : "#e5e7eb");
  const palette = useMemo(
    () => getQuickQuestionPalette(resolvedTextColor, isDark),
    [resolvedTextColor, isDark]
  );

  const applyHoverState = (element, isHover) => {
    element.style.backgroundColor = isHover ? palette.hoverBackground : palette.background;
    element.style.borderColor = isHover ? palette.hoverBorder : palette.border;
    element.style.boxShadow = isHover ? palette.hoverShadow : palette.shadow;
  };

  if (layout === "horizontal") {
    return (
      <div className={`px-4 py-4 border-t ${isDark ? "border-gray-700/50" : "border-gray-200/60"}`}>
        <div
          className={`text-xs mb-3 font-semibold tracking-wide uppercase ${
            isDark ? "text-gray-400" : "text-gray-500"
          }`}
        >
          Quick questions:
        </div>

        <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2">
          {questions.map((question, index) => (
            <button
              key={index}
              onClick={() => onQuestionClick?.(question)}
              className={`group relative text-sm px-5 py-2.5 rounded-xl font-medium transition-all duration-300 hover:scale-105 active:scale-95 whitespace-nowrap flex-shrink-0 overflow-hidden backdrop-blur-sm ${
                isRtl ? "text-right" : "text-left"
              }`}
              style={{
                direction: isRtl ? "rtl" : "ltr",
                backgroundColor: palette.background,
                border: `1.5px solid ${palette.border}`,
                boxShadow: palette.shadow,
              }}
              onMouseEnter={(e) => {
                applyHoverState(e.currentTarget, true);
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                applyHoverState(e.currentTarget, false);
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/0 via-white/5 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
              <span
                className="relative z-10"
                style={{
                  direction: isRtl ? "rtl" : "ltr",
                  color: resolvedTextColor,
                  fontFamily,
                  fontSize,
                  textAlign: isRtl ? "right" : "left",
                }}
              >
                {question}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Vertical layout (default)
  return (
    <div
      className="space-y-3 flex flex-col items-start pl-[50px]"
      {...(isRtl && { style: { paddingLeft: "0", paddingRight: "50px" } })}
    >
      {questions.map((question, index) => (
        <button
          key={index}
          onClick={() => onQuestionClick?.(question)}
          className={`group relative text-sm px-3 py-3 rounded-2xl font-medium transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] whitespace-normal max-w-[85%] overflow-hidden backdrop-blur-sm ${
            isRtl ? "text-right" : "text-left"
          }`}
          style={{
            direction: isRtl ? "rtl" : "ltr",
            backgroundColor: palette.background,
            border: `1.5px solid ${palette.border}`,
            boxShadow: palette.shadow,
          }}
          onMouseEnter={(e) => {
            applyHoverState(e.currentTarget, true);
          }}
          onMouseLeave={(e) => {
            applyHoverState(e.currentTarget, false);
          }}
        >
          <div
            style={{
              backgroundColor: "#F5F5F5",
              border: `1px solid ${resolvedQuestionBoxColor}`,
            }}
            className="absolute inset-0 transition-opacity duration-300 rounded-2xl"
          ></div>
          <span className="relative z-10 flex items-center gap-2.5 text-left">
            <span
              className={`flex-1 leading-snug ${isRtl ? "text-right" : "text-left"}`}
              style={{
                direction: isRtl ? "rtl" : "ltr",
                color: "#1A1A1A",
                fontFamily,
                fontSize,
              }}
            >
              {question}
            </span>
          </span>
        </button>
      ))}
    </div>
  );
};

export default QuickQuestions;
